/**
 * Practitioner assessment APIs for running assessments during a session (consultation/therapy/screening).
 * Event context: use bookedEventId when on assessment making page (current event); use parentBookedEventId when in assessment drawer (parent session event).
 * See docs/portals/mentor/practitioner-assessment.md and practitioner-assessment-runs-client.md
 */
import axios from 'axios'
import {
  getMPDataWithoutParams,
  getMPDataWithParams,
  postMPDataWithoutParams,
  mentorApiURL,
  commonHeadersMPWeb,
  getMainLanguageCode,
  getCountryCode,
} from 'src/mentor/services'
import { isCommonResponseSuccessful, getDataMapFromCommonResponse } from 'src/mentor/helpers/utilityFunctions'
import { buildFreeFormRichTextEmrSection } from 'src/mentor/components/EmrRelatedComponents/FreeFormRichText/freeFormRichTextUtils'

const BASE = '/api/secure/practitioner/assessments'
const ASSESSMENT_REPORT_SAVE = '/api/secure/patient/assessment-report/save'
const ASSESSMENT_REPORT_EMR_SAVE = '/api/secure/mentor/emr-reports/save?reportType=assessment-report'
const ASSESSMENT_REPORT_GET_MENTOR = '/api/secure/mentor/assessment-report/get-by-eventId'
const ASSESSMENT_REPORT_DOWNLOAD = '/api/secure/mentor/emr-reports/download'
const PRACTITIONER_ASSESSMENT_ACCESS_DETAILS_CACHE_TTL_MS = 5 * 60 * 1000

const practitionerAssessmentAccessDetailsCache = {
  data: null,
  timestamp: 0,
}
let inFlightPractitionerAssessmentAccessDetailsPromise = null

/**
 * Access with resolved list details (titles + assessments the mentor can conduct).
 * GET /api/secure/practitioner/assessments/access/details
 * @returns {Promise<{ allowedToAssess: boolean, assessmentLists: Array<{ id, title, assessments: Array<{ id, slug, title }> }> }>}
 */
export async function getPractitionerAssessmentAccessDetails() {
  const hasFreshCache =
    practitionerAssessmentAccessDetailsCache.data &&
    Date.now() - practitionerAssessmentAccessDetailsCache.timestamp < PRACTITIONER_ASSESSMENT_ACCESS_DETAILS_CACHE_TTL_MS
  if (hasFreshCache) {
    return practitionerAssessmentAccessDetailsCache.data
  }

  if (inFlightPractitionerAssessmentAccessDetailsPromise) {
    return inFlightPractitionerAssessmentAccessDetailsPromise
  }

  inFlightPractitionerAssessmentAccessDetailsPromise = (async () => {
    const res = await getMPDataWithoutParams(`${BASE}/access/details`)
    if (res?.status !== 200 || !isCommonResponseSuccessful(res?.data?.code)) {
      return { allowedToAssess: false, assessmentLists: [] }
    }
    const data = res?.data?.data ?? res?.data
    const lists = data?.assessmentAccess?.assessmentLists ?? data?.assessmentLists ?? []
    const normalized = {
      allowedToAssess: Boolean(data?.assessmentAccess?.allowedToAssess ?? data?.allowedToAssess ?? false),
      assessmentLists: Array.isArray(lists) ? lists : [],
    }
    practitionerAssessmentAccessDetailsCache.data = normalized
    practitionerAssessmentAccessDetailsCache.timestamp = Date.now()
    return normalized
  })()

  try {
    return await inFlightPractitionerAssessmentAccessDetailsPromise
  } finally {
    inFlightPractitionerAssessmentAccessDetailsPromise = null
  }
}

/**
 * Check if practitioner can assess and get allowed assessment lists.
 * @returns {Promise<{ allowedToAssess: boolean, assessmentListAllowed: Array }>}
 */
export async function getPractitionerAssessmentAccess() {
  const res = await getMPDataWithoutParams(`${BASE}/access`)
  const code = res?.data?.code
  const payload = res?.data?.data ?? res?.data
  if (res?.status !== 200 || !isCommonResponseSuccessful(code)) {
    return { allowedToAssess: false, assessmentListAllowed: [] }
  }
  return {
    allowedToAssess: Boolean(payload?.assessmentAccess?.allowedToAssess ?? payload?.allowedToAssess ?? false),
    assessmentListAllowed:
      payload?.assessmentAccess?.assessmentListAllowed ?? payload?.assessmentListAllowed ?? [],
  }
}

// Per practitioner-assessment.md: includeAssessmentTests=true required for list + test payload; QnA only when qnaUnlocked=true.
const DEFAULT_EVENT_PARAMS = {
  includePricing: true,
  includeAssessmentTests: true,
}

/**
 * Build query params for event-scoped APIs: send bookedEventId (assessment making page) or parentBookedEventId (drawer).
 * @param {{ bookedEventId?: string, parentBookedEventId?: string }} eventParam - Exactly one: bookedEventId for page, parentBookedEventId for drawer
 */
function eventQueryParam(eventParam) {
  if (eventParam?.parentBookedEventId) return { parentBookedEventId: eventParam.parentBookedEventId }
  if (eventParam?.bookedEventId) return { bookedEventId: eventParam.bookedEventId }
  return {}
}

/**
 * Fetch event assessments (clinical assessments + specs) for the session event.
 * GET .../event?bookedEventId=... or ?parentBookedEventId=... (bookedEventId when on assessment making page; parentBookedEventId when in drawer).
 * @param {{ bookedEventId?: string, parentBookedEventId?: string }} eventParam - bookedEventId = event for which page was opened; parentBookedEventId = parent when in drawer
 * @param {boolean} [includeNonRunnable=false] - Include non-runnable assessments (response may include runnable, runnableReason)
 * @param {boolean} [includeSelfConducted=false] - Include self-conducted assessments in list
 * @returns {Promise<{ eventAssessments: Array }>}
 */
export async function getEventAssessments(eventParam, includeNonRunnable = false, includeSelfConducted = false) {
  const eventQuery = typeof eventParam === 'string' ? { parentBookedEventId: eventParam } : eventQueryParam(eventParam)
  const res = await getMPDataWithParams(`${BASE}/event`, {
    ...eventQuery,
    ...DEFAULT_EVENT_PARAMS,
    includeNonRunnable: Boolean(includeNonRunnable),
    includeSelfConducted: Boolean(includeSelfConducted),
  })
  const code = res?.data?.code
  const dataObj = res?.data?.data ?? res?.data
  if (res?.status !== 200 || !isCommonResponseSuccessful(code)) {
    return { eventAssessments: [] }
  }
  return {
    eventAssessments: dataObj?.eventAssessments ?? [],
  }
}

/**
 * Fetch available assessments for the practitioner (not limited to event config).
 * Use when parent event has no pre-attached clinical assessments; booking auto-attaches.
 * GET .../available?bookedEventId=... or ?parentBookedEventId=... (bookedEventId when on assessment making page; parentBookedEventId when in drawer).
 * @param {{ bookedEventId?: string, parentBookedEventId?: string }} eventParam - bookedEventId = event for which page was opened; parentBookedEventId = parent when in drawer
 * @param {boolean} [includeNonRunnable=false] - Include assessments that are not runnable (response may include runnable, runnableReason)
 * @param {boolean} [includeSelfConducted=false] - Include self-conducted assessments in list (save/run still requires practitioner-conducted)
 * @returns {Promise<{ eventAssessments: Array }>}
 */
export async function getAvailableAssessments(eventParam, includeNonRunnable = false, includeSelfConducted = false) {
  const eventQuery = typeof eventParam === 'string' ? { parentBookedEventId: eventParam } : eventQueryParam(eventParam)
  const res = await getMPDataWithParams(`${BASE}/available`, {
    ...eventQuery,
    ...DEFAULT_EVENT_PARAMS,
    includeNonRunnable: Boolean(includeNonRunnable),
    includeSelfConducted: Boolean(includeSelfConducted),
  })
  const code = res?.data?.code
  const dataObj = res?.data?.data ?? res?.data
  const list = dataObj?.eventAssessments ?? []
  if (res?.status !== 200 || !isCommonResponseSuccessful(code)) {
    return { eventAssessments: [] }
  }
  return {
    eventAssessments: list,
  }
}

/**
 * Fetch single assessment detail (e.g. for details screen). Returns assessment with journalQusAns when qnaUnlocked.
 * GET .../detail?id=&bookedEventId= (or triggeringBookedEventId)
 * Per practitioner-assessment.md: call after booking with bookedEventId to get unlocked payload.
 * @param {string} assessmentId - Assessment test id (id of the assessment test)
 * @param {string} bookedEventId - Parent session event id (alias: triggeringBookedEventId)
 * @returns {Promise<{ assessment?: Object }>}
 */
export async function getAssessmentDetail(assessmentId, bookedEventId) {
  const params = { id: assessmentId }
  if (bookedEventId) params.bookedEventId = bookedEventId
  const res = await getMPDataWithParams(`${BASE}/detail`, params)
  const dataObj = res?.data?.data ?? res?.data
  if (res?.status !== 200 || !isCommonResponseSuccessful(res?.data?.code)) {
    return { assessment: null }
  }
  return {
    assessment: dataObj?.assessment ?? dataObj?.assessmentTest ?? dataObj ?? null,
  }
}

/**
 * Fetch runner context (assessment + booking gate + existing runs + latest answers). No re-book.
 * When from drawer send parentBookedEventId and bookedEventId (assessment event id when known); when from assessment page send bookedEventId.
 * GET .../run-context?parentBookedEventId=...&bookedEventId=...&clinicalAssessmentId=...&assessmentId=... or ?bookedEventId=... (assessment page)
 * @param {Object} params
 * @param {string} [params.bookedEventId] - Assessment event id (sent in addition to parentBookedEventId when from drawer)
 * @param {string} [params.parentBookedEventId] - Parent session event (therapy/consultation/screening)
 * @param {string} [params.assessmentEventBookedEventId] - Legacy: same as bookedEventId (assessment report page)
 * @param {string} params.clinicalAssessmentId - Clinical assessment id
 * @param {string} params.assessmentId - Assessment test id
 * @param {string} [params.languageCode]
 * @returns {Promise<{ assessment?: Object, qnaUnlocked?: boolean, assessmentBookedEventId?: string, ... }>}
 */
export async function getRunContext(params) {
  const { bookedEventId, parentBookedEventId, assessmentEventBookedEventId, clinicalAssessmentId, assessmentId, languageCode } = params || {}
  if (!clinicalAssessmentId) {
    return {
      assessment: null,
      assessmentSpec: null,
      runnerType: null,
      customProtocolDescriptor: null,
      qnaUnlocked: false,
      assessmentRuns: [],
      latestRunAnswers: {},
      latestRunStructuredResponses: {},
      latestRunCustomRunData: {},
      latestRunDerivedMetrics: {},
      latestDraftAnswers: {},
      latestDraftStructuredResponses: {},
      latestDraftCustomRunData: {},
      latestDraftRunId: null,
      latestDraftRunRevision: null,
      latestCompletedRunId: null,
      latestCompletedAssessmentRun: null,
      latestCompletedStructuredResponses: {},
      latestCompletedCustomRunData: {},
      latestCompletedDerivedMetrics: {},
      assessmentCompleted: false,
    }
  }
  const query = { clinicalAssessmentId }
  if (assessmentId) query.assessmentId = assessmentId
  const assessmentEventId = bookedEventId ?? assessmentEventBookedEventId
  if (assessmentEventId) query.bookedEventId = assessmentEventId
  if (parentBookedEventId) query.parentBookedEventId = parentBookedEventId
  if (languageCode) query.languageCode = languageCode
  const res = await getMPDataWithParams(`${BASE}/run-context`, query)
  const dataObj = res?.data?.data ?? res?.data
  if (res?.status !== 200 || !isCommonResponseSuccessful(res?.data?.code)) {
    return {
      assessment: null,
      assessmentSpec: null,
      runnerType: null,
      customProtocolDescriptor: null,
      qnaUnlocked: false,
      assessmentRuns: [],
      latestRunAnswers: {},
      latestRunStructuredResponses: {},
      latestRunCustomRunData: {},
      latestRunDerivedMetrics: {},
      latestDraftAnswers: {},
      latestDraftStructuredResponses: {},
      latestDraftCustomRunData: {},
      latestDraftRunId: null,
      latestDraftRunRevision: null,
      latestCompletedRunId: null,
      latestCompletedAssessmentRun: null,
      latestCompletedStructuredResponses: {},
      latestCompletedCustomRunData: {},
      latestCompletedDerivedMetrics: {},
      assessmentCompleted: false,
    }
  }
  const latestDraftRevisionRaw =
    dataObj?.latestDraftRunRevision ??
    dataObj?.draftRevision ??
    dataObj?.assessmentRunDraft?.draftRevision ??
    null
  return {
    assessment: dataObj?.assessment ?? null,
    assessmentSpec: dataObj?.assessmentSpec ?? null,
    runnerType: dataObj?.runnerType ?? dataObj?.assessmentSpec?.runnerType ?? null,
    customProtocolDescriptor:
      dataObj?.customProtocolDescriptor ?? dataObj?.assessmentSpec?.customProtocol ?? null,
    qnaUnlocked: dataObj?.qnaUnlocked ?? false,
    qnaLockedReason: dataObj?.qnaLockedReason ?? null,
    assessmentRuns: dataObj?.assessmentRuns ?? [],
    latestAssessmentRun: dataObj?.latestAssessmentRun ?? null,
    latestRunAnswers: dataObj?.latestRunAnswers ?? {},
    latestRunStructuredResponses: dataObj?.latestRunStructuredResponses ?? {},
    latestRunCustomRunData: dataObj?.latestRunCustomRunData ?? {},
    latestRunDerivedMetrics: dataObj?.latestRunDerivedMetrics ?? {},
    latestDraftAnswers: dataObj?.latestDraftAnswers ?? {},
    latestDraftStructuredResponses: dataObj?.latestDraftStructuredResponses ?? {},
    latestDraftCustomRunData: dataObj?.latestDraftCustomRunData ?? {},
    latestDraftRunId: dataObj?.latestDraftRunId ?? dataObj?.assessmentRunDraft?.id ?? null,
    latestDraftRunRevision:
      latestDraftRevisionRaw == null || Number.isNaN(Number(latestDraftRevisionRaw))
        ? null
        : Number(latestDraftRevisionRaw),
    latestCompletedRunId:
      dataObj?.latestCompletedRunId ??
      dataObj?.latestCompletedAssessmentRun?.id ??
      dataObj?.latestCompletedAssessmentRun?.assessmentRunId ??
      null,
    latestCompletedAssessmentRun: dataObj?.latestCompletedAssessmentRun ?? null,
    latestCompletedStructuredResponses: dataObj?.latestCompletedStructuredResponses ?? {},
    latestCompletedCustomRunData: dataObj?.latestCompletedCustomRunData ?? {},
    latestCompletedDerivedMetrics: dataObj?.latestCompletedDerivedMetrics ?? {},
    assessmentCompleted: dataObj?.assessmentCompleted === true,
    assessmentBookedEventId: dataObj?.assessmentBookedEventId ?? dataObj?.assessmentBookedEvent?.id ?? null,
  }
}

/**
 * Check if clinic assessment appointment is configured (optional pre-check).
 * GET .../booking-availability?parentBookedEventId=... or ?bookedEventId=... (parentBookedEventId when in drawer; bookedEventId when on page).
 * @param {{ bookedEventId?: string, parentBookedEventId?: string }} eventParam - parentBookedEventId when in drawer; bookedEventId when on assessment making page
 * @param {string} [clinicalAssessmentId]
 * @returns {Promise<{ appointmentConfigured: boolean, reason?: string, code?: number, message?: string }>}
 */
export async function getBookingAvailability(eventParam, clinicalAssessmentId) {
  const eventQuery = typeof eventParam === 'string' ? { parentBookedEventId: eventParam } : eventQueryParam(eventParam)
  const params = { ...eventQuery }
  if (clinicalAssessmentId) params.clinicalAssessmentId = clinicalAssessmentId
  const res = await getMPDataWithParams(`${BASE}/booking-availability`, params)
  const code = res?.data?.code
  const dataObj = res?.data?.data ?? res?.data
  if (res?.status !== 200 || !isCommonResponseSuccessful(code)) {
    return {
      appointmentConfigured: false,
      reason: res?.data?.message,
      code,
      message: res?.data?.message,
    }
  }
  const avail = dataObj?.bookingAvailability ?? dataObj
  return {
    appointmentConfigured: avail?.appointmentConfigured ?? dataObj?.appointmentConfigured ?? false,
    reason: avail?.reason ?? dataObj?.reason ?? null,
    code,
    message: res?.data?.message,
  }
}

/**
 * Fetch assessment runs (results + articles) for a booked event.
 * GET .../runs?parentBookedEventId=...&clinicalAssessmentId=... or ?bookedEventId=...&clinicalAssessmentId=...
 * Use parentBookedEventId when drawer is opened from therapy/consultation/screening (parent session).
 * Use bookedEventId/assessmentEventBookedEventId when on the assessment event page (assessment report making page).
 * @param {Object} params
 * @param {string} [params.triggeringBookedEventId] - Legacy alias; used as parent or assessment event id
 * @param {string} [params.parentBookedEventId] - Parent session event (therapy/consultation/screening)
 * @param {string} [params.assessmentEventBookedEventId] - Assessment booked event (when on assessment page)
 * @param {string} [params.bookedEventId] - Alias for assessmentEventBookedEventId
 * @param {string} [params.clinicalAssessmentId] - Optional filter by clinical assessment
 * @param {boolean} [params.includeDrafts=false] - When true, includes latest draft markers/runs along with completed data
 * @returns {Promise<{ assessmentRuns: Array, assessmentReport?: Object|null, assessmentReports?: Array, assessmentStatuses?: Array, assessmentReportId?: string|null, bookedEventId?: string|null, parentBookedEventId?: string|null, includeDrafts?: boolean }>}
 */
export async function getAssessmentRuns(params) {
  const legacyId = typeof params === 'string' ? params : params?.triggeringBookedEventId
  const parentId = typeof params === 'object' ? params?.parentBookedEventId : undefined
  const assessmentEventId = typeof params === 'object' ? (params?.bookedEventId ?? params?.assessmentEventBookedEventId) : undefined
  const clinicalId = typeof params === 'object' ? params?.clinicalAssessmentId : undefined
  const includeDrafts = typeof params === 'object' ? params?.includeDrafts : undefined
  const query = {}
  if (parentId) query.parentBookedEventId = parentId
  if (assessmentEventId) query.bookedEventId = assessmentEventId
  if (!parentId && !assessmentEventId && legacyId) query.parentBookedEventId = legacyId
  if (clinicalId) query.clinicalAssessmentId = clinicalId
  if (typeof includeDrafts === 'boolean') query.includeDrafts = includeDrafts
  const res = await getMPDataWithParams(`${BASE}/runs`, query)
  const dataObj = res?.data?.data ?? res?.data
  if (res?.status !== 200 || !isCommonResponseSuccessful(res?.data?.code)) {
    return {
      assessmentRuns: [],
      assessmentReport: null,
      assessmentReports: [],
      assessmentStatuses: [],
      assessmentReportId: null,
      bookedEventId: null,
      parentBookedEventId: null,
      includeDrafts: Boolean(includeDrafts),
    }
  }
  const reportsFromArray = Array.isArray(dataObj?.assessmentReports) ? dataObj.assessmentReports : []
  const reportFromAlias = dataObj?.assessmentReport ?? null
  const selectedReport = reportFromAlias ?? reportsFromArray?.[0] ?? null

  const mergedRunsById = new Map()
  const pushRun = (run) => {
    if (!run || typeof run !== 'object') return
    const runId = run?.assessmentRunId ?? run?.id ?? null
    if (runId && mergedRunsById.has(runId)) return
    if (runId) mergedRunsById.set(runId, run)
    else mergedRunsById.set(`idx-${mergedRunsById.size}`, run)
  }
  ;(dataObj?.assessmentRuns ?? []).forEach(pushRun)
  ;(reportFromAlias?.assessmentRuns ?? []).forEach(pushRun)
  reportsFromArray.forEach((reportItem) => {
    ;(reportItem?.assessmentRuns ?? []).forEach(pushRun)
  })

  return {
    assessmentRuns: Array.from(mergedRunsById.values()),
    assessmentReport: selectedReport,
    assessmentReports: reportsFromArray,
    assessmentStatuses: dataObj?.assessmentStatuses ?? [],
    assessmentReportId: dataObj?.assessmentReportId ?? selectedReport?.id ?? null,
    bookedEventId: dataObj?.bookedEventId ?? selectedReport?.bookedEventId ?? null,
    parentBookedEventId: dataObj?.parentBookedEventId ?? null,
    includeDrafts: Boolean(dataObj?.includeDrafts ?? includeDrafts),
  }
}

/**
 * Book an assessment under the current (parent) session event (therapy/consultation/screening).
 * Sends parentBookedEventId to remove confusion with triggering event wording.
 * @param {Object} params
 * @param {string} [params.parentBookedEventId] - Parent session event id (therapy/consultation/screening)
 * @param {string} [params.bookedEventId] - Legacy alias for parentBookedEventId
 * @param {string} [params.triggeringBookedEventId] - Legacy alias for parentBookedEventId
 * @param {string} params.clinicalAssessmentId - Clinical assessment id (alias: psychAssessmentId)
 * @param {string} [params.assessmentId] - Omit for bundle; optional for single
 * @param {string} [params.bookingType='paid'] - 'paid' | 'free'
 * @param {number} [params.feeAmount] - Optional; when bookingType=paid
 * @param {string} [params.startTime] - Optional; ISO-8601 UTC instant; if omitted, server uses parent startTime or current time
 * @param {boolean} [params.includeAuxData=false] - When true, response includes assessment detail (assessment/assessmentDetail, qnaUnlocked) so client can skip detail API
 * @param {string} [params.languageCode='en'] - Used when resolving inline assessment detail
 * @param {boolean} [params.sendPaymentLinkToPatient=true]
 * @param {boolean} [params.sendPaymentLinkToPatientRelative=false]
 * @param {string[]} [params.phoneNumbersToSendThePaymentLink]
 */
export async function bookAssessment(params) {
  const {
    parentBookedEventId,
    bookedEventId,
    triggeringBookedEventId,
    psychAssessmentId,
    clinicalAssessmentId,
    ...rest
  } = params
  const parentId = parentBookedEventId ?? bookedEventId ?? triggeringBookedEventId
  const payload = {
    parentBookedEventId: parentId,
    clinicalAssessmentId: clinicalAssessmentId ?? psychAssessmentId,
    sendPaymentLinkToPatient: true,
    sendPaymentLinkToPatientRelative: false,
    phoneNumbersToSendThePaymentLink: [],
    ...rest,
  }
  if (payload.assessmentId === undefined && params.assessmentId !== undefined) payload.assessmentId = params.assessmentId
  return postMPDataWithoutParams(`${BASE}/book`, payload)
}

/**
 * Submit a practitioner-run assessment. Request body: bookedEventId (mandatory), parentBookedEventId (only when run from drawer).
 * @param {Object} params
 * @param {string} params.bookedEventId - Assessment event id (mandatory). From assessment/book response (drawer) or session event (assessment report page).
 * @param {string} [params.parentBookedEventId] - Parent session event (therapy/consultation/screening). Send only when run is started from Assessment drawer.
 * @param {string} params.clinicalAssessmentId - Clinical assessment id (alias: psychAssessmentId)
 * @param {string} params.assessmentId
 * @param {Record<string, string|string[]>} params.answers - itemId -> value
 * @param {string} [params.completedAt] - ISO date; server sets now if omitted
 * @param {string} [params.visibility] - patient | team | private
 */
export async function submitAssessmentRun(params) {
  const { bookedEventId, parentBookedEventId, psychAssessmentId, clinicalAssessmentId, ...rest } = params
  const payload = {
    bookedEventId,
    clinicalAssessmentId: clinicalAssessmentId ?? psychAssessmentId,
    ...rest,
  }
  if (parentBookedEventId) payload.parentBookedEventId = parentBookedEventId
  return postMPDataWithoutParams(`${BASE}/run`, payload)
}

/**
 * Patch/save practitioner assessment draft progress.
 * PATCH /api/secure/practitioner/assessments/run/progress-draft
 * First save: send bookedEventId + clinicalAssessmentId + assessmentId (+ optional parentBookedEventId) with answersPatch.
 * Subsequent saves: send draftRunId + draftRevision with incremental answersPatch/removeAnswerItemIds.
 * @param {Object} params
 * @returns {Promise<{ success: boolean, conflict?: boolean, assessmentCompleted?: boolean, code?: number, message?: string, data?: Object, draftRunId?: string, draftRevision?: number, latestDraftAnswers?: Object, latestDraftStructuredResponses?: Object, latestDraftCustomRunData?: Object }>}
 */
export async function saveAssessmentRunProgressDraft(params) {
  if (!params || typeof params !== 'object') {
    return { success: false, code: 4003, message: 'Invalid draft payload' }
  }
  const token = localStorage.getItem('token')
  try {
    const res = await axios.patch(
      `${mentorApiURL}${BASE}/run/progress-draft`,
      params,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...commonHeadersMPWeb,
          'ef-user-country-code': getCountryCode(),
          'ef-app-language': getMainLanguageCode(),
        },
      },
    )
    const code = res?.data?.code
    const message = res?.data?.message
    const dataObj = res?.data?.data ?? res?.data ?? {}
    const draftRevisionRaw =
      dataObj?.draftRevision ??
      dataObj?.latestDraftRunRevision ??
      dataObj?.assessmentRunDraft?.draftRevision ??
      null
    if (res?.status === 200 && isCommonResponseSuccessful(code)) {
      return {
        success: true,
        code,
        message,
        data: dataObj,
        draftRunId: dataObj?.draftRunId ?? dataObj?.assessmentRunDraft?.id ?? null,
        draftRevision:
          draftRevisionRaw == null || Number.isNaN(Number(draftRevisionRaw))
            ? null
            : Number(draftRevisionRaw),
        latestDraftAnswers: dataObj?.latestDraftAnswers ?? dataObj?.assessmentRunDraft?.run?.answers ?? {},
        latestDraftStructuredResponses:
          dataObj?.latestDraftStructuredResponses ?? dataObj?.assessmentRunDraft?.run?.structuredResponses ?? {},
        latestDraftCustomRunData:
          dataObj?.latestDraftCustomRunData ?? dataObj?.assessmentRunDraft?.run?.customRunData ?? {},
        assessmentCompleted: dataObj?.assessmentCompleted === true,
      }
    }
    return {
      success: false,
      code,
      message,
      data: dataObj,
      conflict: code === 40991,
      assessmentCompleted: dataObj?.assessmentCompleted === true || code === 4038,
      draftRunId: dataObj?.draftRunId ?? dataObj?.assessmentRunDraft?.id ?? null,
      draftRevision:
        draftRevisionRaw == null || Number.isNaN(Number(draftRevisionRaw))
          ? null
          : Number(draftRevisionRaw),
      latestDraftAnswers: dataObj?.latestDraftAnswers ?? dataObj?.assessmentRunDraft?.run?.answers ?? {},
      latestDraftStructuredResponses:
        dataObj?.latestDraftStructuredResponses ?? dataObj?.assessmentRunDraft?.run?.structuredResponses ?? {},
      latestDraftCustomRunData:
        dataObj?.latestDraftCustomRunData ?? dataObj?.assessmentRunDraft?.run?.customRunData ?? {},
    }
  } catch (e) {
    const status = e?.response?.status
    const code = e?.response?.data?.code
    const message = e?.response?.data?.message
    const dataObj = e?.response?.data?.data ?? {}
    const draftRevisionRaw =
      dataObj?.draftRevision ??
      dataObj?.latestDraftRunRevision ??
      dataObj?.assessmentRunDraft?.draftRevision ??
      null
    return {
      success: false,
      code,
      status,
      message,
      data: dataObj,
      conflict: code === 40991,
      assessmentCompleted: dataObj?.assessmentCompleted === true || code === 4038,
      draftRunId: dataObj?.draftRunId ?? dataObj?.assessmentRunDraft?.id ?? null,
      draftRevision:
        draftRevisionRaw == null || Number.isNaN(Number(draftRevisionRaw))
          ? null
          : Number(draftRevisionRaw),
      latestDraftAnswers: dataObj?.latestDraftAnswers ?? dataObj?.assessmentRunDraft?.run?.answers ?? {},
      latestDraftStructuredResponses:
        dataObj?.latestDraftStructuredResponses ?? dataObj?.assessmentRunDraft?.run?.structuredResponses ?? {},
      latestDraftCustomRunData:
        dataObj?.latestDraftCustomRunData ?? dataObj?.assessmentRunDraft?.run?.customRunData ?? {},
    }
  }
}

/**
 * Fetch a completed run result in save-run response shape.
 * GET /api/secure/practitioner/assessments/run/result?completedRunId=<ARUN-ID>
 * Accepted aliases: runId, assessmentRunId.
 * @param {Object|string} params
 * @param {string} [params.completedRunId] - Preferred run id (ARUN-...)
 * @param {string} [params.runId] - Alias
 * @param {string} [params.assessmentRunId] - Alias
 * @returns {Promise<{ assessmentRun: Object|null, assessmentRunView: Object|null, assessmentReport: Object|null, error?: { code?: number, message?: string } }>}
 */
export async function getAssessmentRunResult(params) {
  const runId =
    typeof params === 'string'
      ? params
      : (params?.completedRunId ?? params?.runId ?? params?.assessmentRunId)
  if (!runId) {
    return {
      assessmentRun: null,
      assessmentRunView: null,
      assessmentReport: null,
      error: { code: 4003, message: 'runId is required' },
    }
  }
  const res = await getMPDataWithParams(`${BASE}/run/result`, { completedRunId: runId })
  const dataObj = res?.data?.data ?? res?.data
  const code = res?.data?.code
  if (res?.status !== 200 || !isCommonResponseSuccessful(code)) {
    return {
      assessmentRun: null,
      assessmentRunView: null,
      assessmentReport: null,
      error: { code, message: res?.data?.message },
    }
  }
  return {
    assessmentRun: dataObj?.assessmentRun ?? null,
    assessmentRunView: dataObj?.assessmentRunView ?? null,
    assessmentReport: dataObj?.assessmentReport ?? null,
  }
}

/**
 * Fetch completed assessment run answer sheet (JSON) for modal.
 * GET /api/secure/practitioner/assessments/run/answer-sheet?runId=<ARUN-ID>
 * See docs/portals/practitioner-assessment-answer-sheet-modal.md
 * @param {string} runId - Assessment run id (ARUN-xxx)
 * @returns {Promise<{ assessmentAnswerSheet: Object }|null>} - null on error or non-success code
 */
export async function getAnswerSheet(runId) {
  if (!runId) return { assessmentAnswerSheet: null, error: { message: 'Missing runId' } }
  const res = await getMPDataWithParams(`${BASE}/run/answer-sheet`, { runId })
  const dataObj = res?.data?.data ?? res?.data
  const code = res?.data?.code
  if (res?.status !== 200 || !isCommonResponseSuccessful(code)) {
    return { assessmentAnswerSheet: null, error: { code, message: res?.data?.message } }
  }
  return { assessmentAnswerSheet: dataObj?.assessmentAnswerSheet ?? dataObj }
}

/**
 * Parse blob as JSON when backend returns 4xx with JSON body (e.g. PDF endpoint with responseType: 'blob').
 * See docs/portals/practitioner-assessment-answer-sheet-modal.md "PDF endpoint error shape".
 */
async function parseBlobAsJsonError(blob) {
  if (!blob || typeof blob?.text !== 'function') return null
  try {
    const text = await blob.text()
    const obj = JSON.parse(text)
    return typeof obj === 'object' && obj !== null ? obj : null
  } catch {
    return null
  }
}

/**
 * Download answer sheet PDF for a completed run.
 * GET /api/secure/practitioner/assessments/run/answer-sheet/pdf?runId=<ARUN-ID>
 * On 4xx, backend returns JSON body (code, message, optional data); we parse blob as JSON to show 4038/4030/4040 messages.
 * See docs/portals/practitioner-assessment-answer-sheet-modal.md
 * @param {string} runId - Assessment run id (ARUN-xxx)
 * @returns {Promise<{ blob: Blob, filename: string }|{ error: { code, message } }>}
 */
export async function getAnswerSheetPdf(runId) {
  if (!runId) return { error: { message: 'Missing runId' } }
  const token = localStorage.getItem('token')
  try {
    const res = await axios.get(`${mentorApiURL}${BASE}/run/answer-sheet/pdf`, {
      params: { runId },
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
        ...commonHeadersMPWeb,
        'ef-user-country-code': getCountryCode(),
        'ef-app-language': getMainLanguageCode(),
      },
    })
    if (res?.status !== 200) {
      const parsed = await parseBlobAsJsonError(res?.data)
      const code = parsed?.code
      const message = parsed?.message ?? 'Failed to download PDF'
      return { error: { code, message } }
    }
    const contentDisposition = res?.headers?.['content-disposition']
    let filename = 'assessment-answer-sheet.pdf'
    if (typeof contentDisposition === 'string' && contentDisposition.includes('filename=')) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
      if (match) filename = match[1].trim()
    }
    return { blob: res.data, filename }
  } catch (err) {
    const data = err?.response?.data
    let code
    let message = 'Failed to download PDF'
    if (data && typeof data?.text === 'function') {
      const parsed = await parseBlobAsJsonError(data)
      if (parsed) {
        code = parsed.code
        if (typeof parsed.message === 'string') message = parsed.message
      }
    } else if (data && typeof data === 'object') {
      code = data.code
      if (typeof data.message === 'string') message = data.message
    }
    return { error: { code, message } }
  }
}

/**
 * Download assessment report PDF by assessment event id.
 * GET /api/secure/mentor/emr-reports/download?reportType=assessment-report&eventId=<EVE-ID>
 * @param {string} bookedEventId - Assessment event id
 * @returns {Promise<{ blob: Blob, filename: string }|{ error: { code?: number, message: string } }>}
 */
export async function getAssessmentReportPdf(bookedEventId) {
  if (!bookedEventId) return { error: { message: 'Missing bookedEventId' } }
  const token = localStorage.getItem('token')
  const countryCode = getCountryCode()
  const languageCode = getMainLanguageCode()
  try {
    const res = await axios.get(`${mentorApiURL}${ASSESSMENT_REPORT_DOWNLOAD}`, {
      params: {
        reportType: 'assessment-report',
        eventId: bookedEventId,
        languageCode,
        platform: 'web',
        countryCode,
      },
      responseType: 'blob',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...commonHeadersMPWeb,
        'ef-user-country-code': countryCode,
        'ef-app-language': languageCode,
      },
    })
    if (res?.status !== 200) {
      const parsed = await parseBlobAsJsonError(res?.data)
      return {
        error: {
          code: parsed?.code,
          message: parsed?.message ?? 'Failed to download report',
        },
      }
    }
    const contentDisposition = res?.headers?.['content-disposition']
    let filename = 'assessment-report.pdf'
    if (typeof contentDisposition === 'string' && contentDisposition.includes('filename=')) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) filename = String(match[1]).replace(/['"]/g, '').trim()
    }
    return { blob: res.data, filename }
  } catch (err) {
    const data = err?.response?.data
    let code
    let message = 'Failed to download report'
    if (data && typeof data?.text === 'function') {
      const parsed = await parseBlobAsJsonError(data)
      if (parsed) {
        code = parsed.code
        if (typeof parsed.message === 'string') message = parsed.message
      }
    } else if (data && typeof data === 'object') {
      code = data.code
      if (typeof data.message === 'string') message = data.message
    }
    return { error: { code, message } }
  }
}

/**
 * Fallback: get AssessmentReport by event id when not returned by GET runs.
 * GET /api/secure/mentor/assessment-report/get-by-eventId?eventId=
 * Response: CommonResponse with data.assessmentReport.
 * @param {string} bookedEventId - Session event id (triggeringBookedEventId)
 * @returns {Promise<{ assessmentReport: Object|null }>}
 */
export async function getAssessmentReportByEventIdMentor(bookedEventId) {
  if (!bookedEventId) return { assessmentReport: null }
  try {
    const res = await getMPDataWithParams(ASSESSMENT_REPORT_GET_MENTOR, { eventId: bookedEventId })
    if (res?.status !== 200 || !isCommonResponseSuccessful(res?.data?.code)) {
      return { assessmentReport: null }
    }
    const data = getDataMapFromCommonResponse(res) ?? res?.data?.data ?? res?.data
    const report = data?.assessmentReport ?? data
    return { assessmentReport: report || null }
  } catch {
    return { assessmentReport: null }
  }
}

/**
 * Save AssessmentReport freeFormRichText (and optional full report body).
 * Primary: POST /api/secure/mentor/emr-reports/save?reportType=assessment-report
 * Fallback: POST /api/secure/patient/assessment-report/save
 * @param {Object} params
 * @param {string} params.bookedEventId
 * @param {string} params.patientId
 * @param {string} params.mentorId
 * @param {string} params.freeFormRichText
 * @param {Object} [params.report] - Existing report (id, answers, summaryAnswers, status, ...) to merge; if omitted, save creates/updates by event
 * @param {'draft'|'completed'|'legacy'} [params.status] - Report state
 * @returns {Promise<{ success: boolean, assessmentReport?: Object, code?: number, message?: string }>}
 */
export async function saveAssessmentReportFreeForm(params) {
  const { bookedEventId, patientId, mentorId, freeFormRichText, report = {}, status } = params || {}
  const resolvedStatus = status ?? report?.status ?? 'draft'
  const assessmentReport = {
    ...report,
    bookedEventId: bookedEventId ?? report?.bookedEventId,
    patientId: patientId ?? report?.patientId,
    mentorId: mentorId ?? report?.mentorId,
    status: resolvedStatus,
    content: freeFormRichText ?? report?.content ?? '',
    freeFormRichText: freeFormRichText ?? report?.freeFormRichText ?? '',
    id: report?.id ?? null,
    assessmentReportId: report?.assessmentReportId ?? report?.id ?? null,
    answers: report?.answers,
    summaryAnswers: report?.summaryAnswers,
    clinicalInterpretation: report?.clinicalInterpretation ?? '',
    functionalImpact: report?.functionalImpact ?? '',
    clinicalObservations: report?.clinicalObservations ?? '',
    provisionalClinicalImpression: report?.provisionalClinicalImpression ?? '',
    recommendations: report?.recommendations ?? '',
    observation: report?.observation ?? '',
    advice: report?.advice ?? '',
  }
  const body = {
    assessmentReport,
    emrSectionDataList: [buildFreeFormRichTextEmrSection(freeFormRichText ?? report?.freeFormRichText ?? '')],
  }
  try {
    const res = await postMPDataWithoutParams(ASSESSMENT_REPORT_EMR_SAVE, body)
    const code = res?.data?.code
    const message = res?.data?.message
    if (res?.status !== 200 || !isCommonResponseSuccessful(res?.data?.code)) {
      return { success: false, code, message }
    }
    const data = getDataMapFromCommonResponse(res) ?? res?.data?.data ?? res?.data
    const saved = data?.assessmentReport ?? data
    return { success: true, assessmentReport: saved, code, message }
  } catch {
    // Backward compatibility fallback for environments still using legacy save endpoint.
    try {
      const legacyBody = {
        ...assessmentReport,
        emrSectionDataList: body.emrSectionDataList,
      }
      const res = await postMPDataWithoutParams(ASSESSMENT_REPORT_SAVE, legacyBody)
      const code = res?.data?.code
      const message = res?.data?.message
      if (res?.status !== 200 || !isCommonResponseSuccessful(res?.data?.code)) {
        return { success: false, code, message }
      }
      const saved = getDataMapFromCommonResponse(res)?.assessmentReport ?? getDataMapFromCommonResponse(res)
      return { success: true, assessmentReport: saved, code, message }
    } catch {
      return { success: false }
    }
  }
}

/**
 * Mark a booked event as started by logging a JOIN action for mentor.
 * Required by backends that allow completion only after start.
 * @param {string} bookedEventId
 * @returns {Promise<{ success: boolean, alreadyStarted?: boolean, code?: number, message?: string }>}
 */
export async function markBookedEventAsStarted(bookedEventId) {
  if (!bookedEventId) return { success: false, message: 'Missing bookedEventId' }
  const token = localStorage.getItem('token')
  const isAlreadyStarted = (code, message) => {
    if (typeof message !== 'string') return false
    const lower = message.toLowerCase()
    return lower.includes('already started') || lower.includes('already joined') || lower.includes('already in progress')
  }
  try {
    const res = await axios.post(
      `${mentorApiURL}/api/secure/live/booked-event/log`,
      {
        bookedEventId,
        action: 'JOIN',
        role: 'mentor',
      },
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...commonHeadersMPWeb,
          'ef-user-country-code': getCountryCode(),
          'ef-app-language': getMainLanguageCode(),
        },
      },
    )
    const code = res?.data?.code
    const message = res?.data?.message
    if (res?.status === 200 && (code == null || isCommonResponseSuccessful(code))) {
      return { success: true, code, message }
    }
    if (res?.status === 400 || isAlreadyStarted(code, message)) {
      return { success: true, alreadyStarted: true, code, message }
    }
    return { success: false, code, message }
  } catch (e) {
    const status = e?.response?.status
    const code = e?.response?.data?.code
    const message = e?.response?.data?.message
    if (status === 400 || isAlreadyStarted(code, message)) {
      return { success: true, alreadyStarted: true, code, message }
    }
    return { success: false, code, message }
  }
}

/**
 * Mark a booked event as completed.
 * Uses teacher finish endpoint (same as session completion action).
 * @param {string} bookedEventId
 * @returns {Promise<{ success: boolean, alreadyCompleted?: boolean, code?: number, message?: string }>}
 */
export async function markBookedEventAsCompleted(bookedEventId) {
  if (!bookedEventId) return { success: false, message: 'Missing bookedEventId' }
  const isAlreadyCompleted = (code, message) => {
    if (code === 4443) return true
    return typeof message === 'string' && message.toLowerCase().includes('already completed')
  }
  const getSessionStatus = (dataObj) => (
    dataObj?.sessionStatus ??
    dataObj?.bookedEvent?.sessionStatus ??
    dataObj?.event?.sessionStatus ??
    null
  )
  const isMarkedCompleted = (sessionStatus) =>
    typeof sessionStatus === 'string' && sessionStatus.toLowerCase() === 'marked-completed'
  try {
    const rawRes = await postMPDataWithoutParams(
      `/api/secure/teacher/booked-event/finish?bookedEventId=${bookedEventId}`,
      {},
    )
    const res = rawRes?.response ?? rawRes
    const status = res?.status
    const code = res?.data?.code
    const message = res?.data?.message
    const dataObj = getDataMapFromCommonResponse(res) ?? res?.data?.data ?? res?.data
    const sessionStatus = getSessionStatus(dataObj)
    if (status === 200 && (isCommonResponseSuccessful(code) || isMarkedCompleted(sessionStatus))) {
      return { success: true, code, message, sessionStatus }
    }
    if (status === 400 || isAlreadyCompleted(code, message)) {
      return { success: true, alreadyCompleted: true, code, message, sessionStatus }
    }
    return { success: false, code, message, sessionStatus }
  } catch (e) {
    const status = e?.response?.status
    const code = e?.response?.data?.code
    const message = e?.response?.data?.message
    const dataObj = e?.response?.data?.data ?? e?.response?.data
    const sessionStatus = getSessionStatus(dataObj)
    if (status === 200 && isMarkedCompleted(sessionStatus)) {
      return { success: true, code, message, sessionStatus }
    }
    if (status === 400 || isAlreadyCompleted(code, message)) {
      return { success: true, alreadyCompleted: true, code, message, sessionStatus }
    }
    return { success: false, code, message, sessionStatus }
  }
}
